import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore } from "@/stores/auth.store";

/**
 * Shared logout handler. One implementation feeds every entry
 * point — the header avatar dropdown and the sidebar's bottom
 * logout icon — so the user-facing behavior (toast, redirect,
 * cache cleanup) can't drift between them.
 *
 * Local-only flow: the auth store's `logout()` action calls
 * `tokenStorage.clear()` (wiping `prism.access_token` and
 * `prism.refresh_token`) AND resets the in-memory store; the
 * persist middleware then rewrites the cleared shape into
 * `prism.auth`. No `/auth/logout` endpoint exists today, so
 * nothing to await or catch.
 *
 * Rapid double-click guard via useRef: the first call flips the
 * ref true and any subsequent invocations within the same React
 * render are no-ops — prevents the second click from firing a
 * second navigate / toast as the component is unmounting.
 */
export function useLogout(): () => Promise<void> {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const inFlightRef = useRef(false);

  return useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    logout();
    navigate("/login", { replace: true });
    toast.success("Logged out");
  }, [logout, navigate]);
}
